// ============================================================================
// POKER 101 MODAL - ENTERPRISE BEGINNER TUTORIAL
// ============================================================================
// Complete 12-step poker tutorial for ABSOLUTE beginners with interactive
// quizzes, progress tracking, and unlockable rewards.
//  MOBILE OPTIMIZED - Responsive design for all screen sizes

// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Progress,
  Badge,
  Grid,
  useToast,
  Collapse,
  Icon,
  Divider,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  Trophy,
  CheckCircle,
  Circle,
  ChevronRight,
  ChevronLeft,
  Star,
  Gift,
  BookOpen,
  Target,
  Zap,
  HelpCircle,
  Award,
  Play,
  Lock,
  Unlock,
  Lightbulb,
  ThumbsUp,
  X,
} from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerButton from './ui/PokerButton';

// ============================================================================
// ANIMATIONS
// ============================================================================

const celebrateAnim = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 211, 238, 0.4); }
  50% { box-shadow: 0 0 20px 4px rgba(34, 211, 238, 0.6); }
`;

const cardFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-8px) rotate(2deg); }
`;

// ============================================================================
// TYPES
// ============================================================================

interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

interface TutorialStep {
  id: string;
  chapter: number;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  quiz?: QuizQuestion;
  visualCards?: string; // Card visualization
  proTip?: string;
}

interface Poker101ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  initialStep?: number;
}

// ============================================================================
// CARD VISUALIZATION COMPONENT - MOBILE OPTIMIZED
// ============================================================================

const CardDisplay: React.FC<{ cards: string; label?: string; size?: 'sm' | 'md' | 'lg'; isMobile?: boolean }> = ({ 
  cards, 
  label,
  size = 'md',
  isMobile = false,
}) => {
  //  MOBILE: Smaller cards on mobile

  const sizeMap = {
    sm: { card: isMobile ? '28px' : '36px', font: isMobile ? 'sm' : 'md', gap: 1 },
    md: { card: isMobile ? '36px' : '48px', font: isMobile ? 'md' : 'lg', gap: isMobile ? 1 : 2 },
    lg: { card: isMobile ? '44px' : '60px', font: isMobile ? 'lg' : 'xl', gap: 2 },
  };
  
  const cardList = cards.split(' ');
  const s = sizeMap[size];
  
  const getSuitColor = (card: string) => {
    if (card.includes('') || card.includes('')) return '#ef4444';

    return 'white';
  };
  
  return (
    <VStack spacing={1}>
      {label && (
        <Text color="gray.400" fontSize={isMobile ? 'xs' : 'sm'} fontWeight="medium">{label}</Text>
      )}
      <HStack spacing={s.gap} flexWrap="wrap" justify="center">
        {cardList.map((card, i) => (
          <Box
            key={i}
            bg="white"
            color={getSuitColor(card)}
            w={s.card}
            h={`calc(${s.card} * 1.4)`}
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontWeight="bold"
            fontSize={s.font}
            boxShadow="lg"
            border="2px solid"
            borderColor="gray.200"
            animation={`${cardFloat} 3s ease-in-out infinite`}
            style={{ animationDelay: `${i * 0.2}s` }}
          >
            {card}
          </Box>
        ))}
      </HStack>
    </VStack>
  );
};

// ============================================================================
// QUIZ COMPONENT - MOBILE OPTIMIZED
// ============================================================================

const QuizSection: React.FC<{
  quiz: QuizQuestion;
  onAnswer: (correct: boolean) => void;
  answered: boolean;
  selectedAnswer: number | null;
  isMobile?: boolean;
}> = ({ quiz, onAnswer, answered, selectedAnswer, isMobile = false }) => {
  const [selected, setSelected] = useState<number | null>(selectedAnswer);
  const [showExplanation, setShowExplanation] = useState(answered);
  
  const handleSelect = (index: number) => {
    if (answered) return;
    setSelected(index);
  };
  
  const handleSubmit = () => {
    if (selected === null) return;
    const correct = selected === quiz.correctIndex;
    setShowExplanation(true);
    onAnswer(correct);
  };
  
  return (
    <VStack spacing={isMobile ? 3 : 4} align="stretch" w="100%">
      <Box
        bg={`${POKER_COLORS.cyan}15`}
        p={isMobile ? 3 : 4}
        borderRadius="lg"
        border="1px solid"
        borderColor={`${POKER_COLORS.cyan}33`}
      >
        <HStack spacing={2} mb={2}>
          <HelpCircle size={isMobile ? 16 : 18} color={POKER_COLORS.cyan} />
          <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize={isMobile ? 'xs' : 'sm'}>QUIZ</Text>
        </HStack>
        <Text color="white" fontSize={isMobile ? 'md' : 'lg'} fontWeight="semibold">
          {quiz.question}
        </Text>
      </Box>
      
      <VStack spacing={2} align="stretch">
        {quiz.options.map((option, index) => {
          const isSelected = selected === index;
          const isCorrect = index === quiz.correctIndex;
          const showResult = showExplanation;
          
          let bgColor = 'blackAlpha.400';
          let borderColor = 'whiteAlpha.200';
          
          if (showResult) {
            if (isCorrect) {
              bgColor = `${POKER_COLORS.darkGreen}33`;
              borderColor = POKER_COLORS.darkGreen;
            } else if (isSelected && !isCorrect) {
              bgColor = `${POKER_COLORS.darkRed}33`;
              borderColor = POKER_COLORS.darkRed;
            }
          } else if (isSelected) {
            bgColor = `${POKER_COLORS.cyan}22`;
            borderColor = POKER_COLORS.cyan;
          }
          
          return (
            <Box
              key={index}
              p={isMobile ? 3 : 4}
              bg={bgColor}
              borderRadius="lg"
              border="2px solid"
              borderColor={borderColor}
              cursor={answered ? 'default' : 'pointer'}
              onClick={() => handleSelect(index)}
              _hover={answered ? {} : { bg: `${POKER_COLORS.cyan}22`, borderColor: POKER_COLORS.cyan }}
              _active={answered ? {} : { transform: 'scale(0.98)' }}
              transition="all 0.2s"
              minH={isMobile ? '44px' : '50px'}
            >
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <HStack spacing={isMobile ? 2 : 3} flex={1}>
                  <Box
                    w={isMobile ? '20px' : '24px'}
                    h={isMobile ? '20px' : '24px'}
                    borderRadius="full"
                    border="2px solid"
                    borderColor={isSelected ? POKER_COLORS.cyan : 'gray.500'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg={isSelected ? POKER_COLORS.cyan : 'transparent'}
                    flexShrink={0}
                  >
                    {isSelected && <CheckCircle size={isMobile ? 12 : 14} color="white" />}
                  </Box>
                  <Text color="white" fontSize={isMobile ? 'sm' : 'md'} fontWeight="medium">
                    {option}
                  </Text>
                </HStack>
                
                {showResult && isCorrect && (
                  <Badge bg={POKER_COLORS.darkGreen} color="white" fontSize="xs">
                     CORRECT

                  </Badge>
                )}
                {showResult && isSelected && !isCorrect && (
                  <Badge bg={POKER_COLORS.darkRed} color="white" fontSize="xs">
                     WRONG

                  </Badge>
                )}
              </HStack>
            </Box>
          );
        })}
      </VStack>
      
      {!answered && (
        <PokerButton
          variant="primary"
          onClick={handleSubmit}
          isDisabled={selected === null}
          w="100%"
          size={isMobile ? 'md' : 'lg'}
        >
          Check Answer
        </PokerButton>
      )}
      
      <Collapse in={showExplanation}>
        <Box
          p={isMobile ? 3 : 4}
          bg={selected === quiz.correctIndex ? `${POKER_COLORS.darkGreen}22` : `${POKER_COLORS.gold}15`}
          borderRadius="lg"
          border="1px solid"
          borderColor={selected === quiz.correctIndex ? POKER_COLORS.darkGreen : POKER_COLORS.gold}
        >
          <HStack spacing={2} mb={2}>
            {selected === quiz.correctIndex ? (
              <ThumbsUp size={isMobile ? 16 : 18} color={POKER_COLORS.darkGreen} />
            ) : (
              <Lightbulb size={isMobile ? 16 : 18} color={POKER_COLORS.gold} />
            )}
            <Text 
              color={selected === quiz.correctIndex ? POKER_COLORS.darkGreen : POKER_COLORS.gold} 
              fontWeight="bold"
              fontSize={isMobile ? 'xs' : 'sm'}
            >
              {selected === quiz.correctIndex ? 'GREAT JOB!' : 'LEARN FROM THIS'}
            </Text>
          </HStack>
          <Text color="white" fontSize={isMobile ? 'sm' : 'md'}>
            {quiz.explanation}
          </Text>
        </Box>
      </Collapse>
    </VStack>
  );
};

// ============================================================================
// TUTORIAL CONTENT
// ============================================================================

const TUTORIAL_STEPS: TutorialStep[] = [
  // ========================================
  // CHAPTER 1: WHAT IS POKER?
  // ========================================
  {
    id: 'intro-1',
    chapter: 1,
    title: 'Welcome to Poker!',
    icon: <Star size={24} color={POKER_COLORS.gold} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
           <strong>Welcome!</strong> Poker is one of the world's most popular card games, 

          combining strategy, skill, and psychology.
        </Text>
        <Box bg={`${POKER_COLORS.cyan}15`} p={{ base: 3, md: 4 }} borderRadius="lg">
          <Text color="white" fontSize={{ base: 'sm', md: 'md' }}>
            <strong>The Goal:</strong> Win chips by having the best hand at showdown 
            OR by making everyone else give up (fold).
          </Text>
        </Box>
        <Text color="gray.300" fontSize={{ base: 'sm', md: 'md' }}>
          Don't worry if you've never played - this tutorial will teach you everything 
          you need to know in about 5 minutes!
        </Text>
      </VStack>
    ),
    proTip: 'Poker is about 50% luck and 50% skill. The more you learn, the more skill matters!',
  },
  {
    id: 'intro-2',
    chapter: 1,
    title: 'Texas Hold\'em',
    icon: <BookOpen size={24} color={POKER_COLORS.cyan} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          We play <strong>Texas Hold'em</strong> - the most popular poker game in the world!
        </Text>
        <Box bg="blackAlpha.400" p={{ base: 3, md: 4 }} borderRadius="lg">
          <VStack spacing={3} align="start">
            <HStack>
              <Badge bg={POKER_COLORS.cyan} color="white" fontSize={{ base: 'xs', md: 'sm' }}>1</Badge>
              <Text color="white" fontSize={{ base: 'sm', md: 'md' }}>You get <strong>2 private cards</strong> (only you see them)</Text>
            </HStack>
            <HStack>
              <Badge bg={POKER_COLORS.cyan} color="white" fontSize={{ base: 'xs', md: 'sm' }}>2</Badge>
              <Text color="white" fontSize={{ base: 'sm', md: 'md' }}><strong>5 community cards</strong> go in the middle (everyone shares)</Text>
            </HStack>
            <HStack>
              <Badge bg={POKER_COLORS.cyan} color="white" fontSize={{ base: 'xs', md: 'sm' }}>3</Badge>
              <Text color="white" fontSize={{ base: 'sm', md: 'md' }}>Make the <strong>best 5-card hand</strong> from any combination</Text>
            </HStack>
          </VStack>
        </Box>
      </VStack>
    ),
    visualCards: 'A K',

    proTip: 'You can use BOTH your cards, ONE of your cards, or even NONE (just the board)!',
  },
  {
    id: 'intro-3',
    chapter: 1,
    title: 'How to Win',
    icon: <Trophy size={24} color={POKER_COLORS.gold} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          There are <strong>TWO ways to win</strong> a poker hand:
        </Text>
        <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={{ base: 3, md: 4 }}>
          <Box bg={`${POKER_COLORS.darkGreen}22`} p={{ base: 3, md: 4 }} borderRadius="lg" border="1px solid" borderColor={POKER_COLORS.darkGreen}>
            <VStack spacing={2}>
              <Trophy size={28} color={POKER_COLORS.gold} />
              <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Best Hand</Text>
              <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }} textAlign="center">
                Have the strongest cards at showdown
              </Text>
            </VStack>
          </Box>
          <Box bg={`${POKER_COLORS.cyan}22`} p={{ base: 3, md: 4 }} borderRadius="lg" border="1px solid" borderColor={POKER_COLORS.cyan}>
            <VStack spacing={2}>
              <Zap size={28} color={POKER_COLORS.cyan} />
              <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Make Them Fold</Text>
              <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }} textAlign="center">
                Bet/raise until everyone gives up
              </Text>
            </VStack>
          </Box>
        </Grid>
        <Box bg={`${POKER_COLORS.gold}15`} p={3} borderRadius="md">
          <Text color={POKER_COLORS.gold} fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
             This is why bluffing works - you can win without the best cards!

          </Text>
        </Box>
      </VStack>
    ),
    quiz: {
      question: 'How can you win a poker hand?',
      options: [
        'Only by having the best cards',
        'Only by bluffing',
        'By having the best hand OR making everyone fold',
        'By betting the most money',
      ],
      correctIndex: 2,
      explanation: 'You can win either by having the best hand at showdown OR by making all opponents fold. Both are legitimate ways to win!',
    },
  },
  
  // ========================================
  // CHAPTER 2: THE CARDS
  // ========================================
  {
    id: 'cards-1',
    chapter: 2,
    title: 'Your Hole Cards',
    icon: <Target size={24} color={POKER_COLORS.cyan} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          At the start of each hand, you receive <strong>2 private cards</strong> called "hole cards."
        </Text>
        <Box bg="blackAlpha.400" p={{ base: 3, md: 4 }} borderRadius="lg">
          <VStack spacing={3}>
            <CardDisplay cards="A K" label="Your Hole Cards (Only You See)" />

            <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }} textAlign="center">
              These are YOUR secret cards. No one else can see them until showdown!
            </Text>
          </VStack>
        </Box>
        <Box bg={`${POKER_COLORS.darkGreen}22`} p={3} borderRadius="md">
          <Text color="white" fontSize={{ base: 'xs', md: 'sm' }}>
            <strong>A K</strong> is called "Ace-King" or "Big Slick" - one of the best starting hands!

          </Text>
        </Box>
      </VStack>
    ),
    proTip: 'Protect your hole cards! Don\'t let others see them until the hand is over.',
  },
  {
    id: 'cards-2',
    chapter: 2,
    title: 'Community Cards',
    icon: <Target size={24} color={POKER_COLORS.cyan} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          The dealer places <strong>5 community cards</strong> face-up in the middle. Everyone can use these!
        </Text>
        <Box bg="blackAlpha.400" p={{ base: 3, md: 4 }} borderRadius="lg">
          <VStack spacing={4}>
            <CardDisplay cards="K Q 8 5 2" label="The Board (Everyone Uses These)" size="md" />

            <HStack spacing={{ base: 2, md: 4 }} flexWrap="wrap" justify="center">
              <Badge bg={POKER_COLORS.cyan} px={{ base: 2, md: 3 }} py={1} fontSize={{ base: 'xs', md: 'sm' }}>
                First 3 = "Flop"
              </Badge>
              <Badge bg={POKER_COLORS.gold} px={{ base: 2, md: 3 }} py={1} fontSize={{ base: 'xs', md: 'sm' }}>
                4th = "Turn"
              </Badge>
              <Badge bg="purple.500" px={{ base: 2, md: 3 }} py={1} fontSize={{ base: 'xs', md: 'sm' }}>
                5th = "River"
              </Badge>
            </HStack>
          </VStack>
        </Box>
        <Text color="gray.300" fontSize={{ base: 'sm', md: 'md' }}>
          Combine YOUR hole cards with the community cards to make your best 5-card hand!
        </Text>
      </VStack>
    ),
    quiz: {
      question: 'The 5 shared cards in the middle are called...',
      options: [
        'Hole cards',
        'Community cards (the board)',
        'Secret cards',
        'Dealer cards',
      ],
      correctIndex: 1,
      explanation: 'The 5 shared cards are called "community cards" or "the board." Everyone uses them to make their hand!',
    },
  },
  {
    id: 'cards-3',
    chapter: 2,
    title: 'Hand Rankings',
    icon: <Award size={24} color={POKER_COLORS.gold} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          Here are the <strong>top 5 poker hands</strong> you need to know:
        </Text>
        <VStack spacing={2} align="stretch">
          {[
            { rank: '1', name: 'Royal Flush', desc: 'A K Q J 10 same suit', color: POKER_COLORS.gold },
            { rank: '2', name: 'Straight Flush', desc: '5 cards in a row, same suit', color: POKER_COLORS.cyan },
            { rank: '3', name: 'Four of a Kind', desc: '4 cards of same value', color: 'purple.400' },
            { rank: '4', name: 'Full House', desc: '3 of a kind + pair', color: 'pink.400' },
            { rank: '5', name: 'Flush', desc: '5 cards same suit', color: 'blue.400' },
          ].map(hand => (
            <HStack 
              key={hand.rank}
              bg="blackAlpha.400" 
              p={{ base: 2, md: 3 }} 
              borderRadius="md"
              border="1px solid"
              borderColor={`${hand.color}44`}
            >
              <Badge bg={hand.color} color="white" w={{ base: '24px', md: '28px' }} textAlign="center">
                {hand.rank}
              </Badge>
              <VStack align="start" spacing={0} flex={1}>
                <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>{hand.name}</Text>
                <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>{hand.desc}</Text>
              </VStack>
            </HStack>
          ))}
        </VStack>
        <Box bg={`${POKER_COLORS.gold}15`} p={3} borderRadius="md">
          <Text color={POKER_COLORS.gold} fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
             Don't worry about memorizing all rankings yet - you'll learn them as you play!

          </Text>
        </Box>
      </VStack>
    ),
    proTip: 'A Pair or Two Pair wins most hands. Royal Flush is very rare!',
  },
  
  // ========================================
  // CHAPTER 3: GAME FLOW
  // ========================================
  {
    id: 'flow-1',
    chapter: 3,
    title: 'Betting Rounds',
    icon: <Play size={24} color={POKER_COLORS.cyan} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          Each hand has <strong>4 betting rounds</strong>:
        </Text>
        <VStack spacing={2} align="stretch">
          {[
            { name: 'Pre-Flop', desc: 'After you get your 2 cards', icon: '1⃣' },

            { name: 'Flop', desc: 'After 3 community cards', icon: '2⃣' },

            { name: 'Turn', desc: 'After 4th community card', icon: '3⃣' },

            { name: 'River', desc: 'After 5th community card', icon: '4⃣' },

          ].map(round => (
            <HStack 
              key={round.name}
              bg="blackAlpha.400" 
              p={{ base: 2, md: 3 }} 
              borderRadius="md"
            >
              <Text fontSize={{ base: 'lg', md: 'xl' }}>{round.icon}</Text>
              <VStack align="start" spacing={0}>
                <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>{round.name}</Text>
                <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>{round.desc}</Text>
              </VStack>
            </HStack>
          ))}
        </VStack>
      </VStack>
    ),
    proTip: 'You can fold at any betting round if you think your hand is weak!',
  },
  {
    id: 'flow-2',
    chapter: 3,
    title: 'Your Actions',
    icon: <Zap size={24} color={POKER_COLORS.cyan} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          When it's your turn, you can:
        </Text>
        <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={{ base: 2, md: 3 }}>
          {[
            { action: 'Check', desc: 'Pass (if no bet)', color: 'gray.500', icon: '' },

            { action: 'Bet', desc: 'Put chips in', color: POKER_COLORS.cyan, icon: '' },

            { action: 'Call', desc: 'Match the bet', color: POKER_COLORS.gold, icon: '' },

            { action: 'Raise', desc: 'Increase the bet', color: 'orange.400', icon: '' },

            { action: 'Fold', desc: 'Give up hand', color: POKER_COLORS.darkRed, icon: '' },

            { action: 'All-In', desc: 'Bet everything!', color: 'purple.400', icon: '' },

          ].map(act => (
            <HStack 
              key={act.action}
              bg={`${act.color}22`} 
              p={{ base: 2, md: 3 }} 
              borderRadius="md"
              border="1px solid"
              borderColor={`${act.color}44`}
            >
              <Text fontSize={{ base: 'md', md: 'lg' }}>{act.icon}</Text>
              <VStack align="start" spacing={0}>
                <Text color="white" fontWeight="bold" fontSize={{ base: 'xs', md: 'sm' }}>{act.action}</Text>
                <Text color="gray.400" fontSize={{ base: '10px', md: 'xs' }}>{act.desc}</Text>
              </VStack>
            </HStack>
          ))}
        </Grid>
      </VStack>
    ),
    quiz: {
      question: 'What does "Fold" mean?',
      options: [
        'Put more chips in the pot',
        'Give up your hand and exit the round',
        'Match the current bet',
        'Bet all your chips',
      ],
      correctIndex: 1,
      explanation: 'Folding means you give up your hand and lose any chips you\'ve already bet. You\'re out until the next hand.',
    },
  },
  {
    id: 'flow-3',
    chapter: 3,
    title: 'Blinds & Positions',
    icon: <Target size={24} color={POKER_COLORS.gold} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          <strong>Blinds</strong> are forced bets that start the action:
        </Text>
        <Box bg="blackAlpha.400" p={{ base: 3, md: 4 }} borderRadius="lg">
          <VStack spacing={3} align="start">
            <HStack>
              <Badge bg={POKER_COLORS.cyan} color="white" fontSize={{ base: 'xs', md: 'sm' }}>SB</Badge>
              <Text color="white" fontSize={{ base: 'sm', md: 'md' }}><strong>Small Blind</strong> - posts half bet</Text>
            </HStack>
            <HStack>
              <Badge bg={POKER_COLORS.gold} color="white" fontSize={{ base: 'xs', md: 'sm' }}>BB</Badge>
              <Text color="white" fontSize={{ base: 'sm', md: 'md' }}><strong>Big Blind</strong> - posts full bet</Text>
            </HStack>
            <HStack>
              <Badge bg="gray.600" color="white" fontSize={{ base: 'xs', md: 'sm' }}>D</Badge>
              <Text color="white" fontSize={{ base: 'sm', md: 'md' }}><strong>Dealer Button</strong> - best position!</Text>
            </HStack>
          </VStack>
        </Box>
        <Box bg={`${POKER_COLORS.gold}15`} p={3} borderRadius="md">
          <Text color={POKER_COLORS.gold} fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
             Acting LAST is a huge advantage - you see what everyone else does first!

          </Text>
        </Box>
      </VStack>
    ),
    proTip: 'The dealer position (Button) is the best seat because you act last!',
  },
  
  // ========================================
  // CHAPTER 4: BASIC STRATEGY
  // ========================================
  {
    id: 'strategy-1',
    chapter: 4,
    title: 'Starting Hands',
    icon: <Star size={24} color={POKER_COLORS.gold} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          <strong>Not all hands are equal!</strong> Here are the best starting hands:
        </Text>
        <Box bg="blackAlpha.400" p={{ base: 3, md: 4 }} borderRadius="lg">
          <VStack spacing={3}>
            <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}> Premium Hands (Always Play)</Text>

            <HStack spacing={{ base: 2, md: 4 }} flexWrap="wrap" justify="center">
              <Badge bg={POKER_COLORS.gold} px={2} py={1}>AA</Badge>
              <Badge bg={POKER_COLORS.gold} px={2} py={1}>KK</Badge>
              <Badge bg={POKER_COLORS.gold} px={2} py={1}>QQ</Badge>
              <Badge bg={POKER_COLORS.gold} px={2} py={1}>AK</Badge>
            </HStack>
            
            <Divider borderColor="gray.600" />
            
            <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}> Good Hands (Usually Play)</Text>

            <HStack spacing={{ base: 2, md: 4 }} flexWrap="wrap" justify="center">
              <Badge bg={POKER_COLORS.cyan} px={2} py={1}>JJ</Badge>
              <Badge bg={POKER_COLORS.cyan} px={2} py={1}>TT</Badge>
              <Badge bg={POKER_COLORS.cyan} px={2} py={1}>AQ</Badge>
              <Badge bg={POKER_COLORS.cyan} px={2} py={1}>KQ</Badge>
            </HStack>
          </VStack>
        </Box>
        <Box bg={`${POKER_COLORS.darkRed}22`} p={3} borderRadius="md" border="1px solid" borderColor={POKER_COLORS.darkRed}>
          <Text color={POKER_COLORS.darkRed} fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
             Fold weak hands like 7-2, 8-3, 9-4 - they rarely win!

          </Text>
        </Box>
      </VStack>
    ),
    quiz: {
      question: 'Which is the best starting hand?',
      options: [
        '7 2',

        'A A',

        'K 3',

        'Q 5',

      ],
      correctIndex: 1,
      explanation: 'Pocket Aces (A A) is the best starting hand in Texas Hold\'em! It wins against any other starting hand.',

    },
  },
  {
    id: 'strategy-2',
    chapter: 4,
    title: 'Position Matters',
    icon: <Target size={24} color={POKER_COLORS.cyan} />,
    content: (
      <VStack spacing={4} align="stretch">
        <Text color="white" fontSize={{ base: 'md', md: 'lg' }}>
          <strong>Position is EVERYTHING</strong> in poker:
        </Text>
        <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={{ base: 3, md: 4 }}>
          <Box bg={`${POKER_COLORS.darkGreen}22`} p={{ base: 3, md: 4 }} borderRadius="lg" border="1px solid" borderColor={POKER_COLORS.darkGreen}>
            <VStack>
              <Text fontSize={{ base: 'xl', md: '2xl' }}></Text>

              <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Late Position</Text>
              <Text color={POKER_COLORS.darkGreen} fontSize={{ base: 'xs', md: 'sm' }} textAlign="center">
                Act LAST → See what others do → GOOD!
              </Text>
            </VStack>
          </Box>
          <Box bg={`${POKER_COLORS.darkRed}22`} p={{ base: 3, md: 4 }} borderRadius="lg" border="1px solid" borderColor={POKER_COLORS.darkRed}>
            <VStack>
              <Text fontSize={{ base: 'xl', md: '2xl' }}></Text>

              <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Early Position</Text>
              <Text color={POKER_COLORS.darkRed} fontSize={{ base: 'xs', md: 'sm' }} textAlign="center">
                Act FIRST → Blind decisions → RISKY!
              </Text>
            </VStack>
          </Box>
        </Grid>
        <Box bg={`${POKER_COLORS.gold}15`} p={3} borderRadius="md">
          <Text color={POKER_COLORS.gold} fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
             Play MORE hands in late position, FEWER in early position!

          </Text>
        </Box>
      </VStack>
    ),
    proTip: 'The Button (Dealer) is the best position - you act last on every street except pre-flop!',
  },
  {
    id: 'strategy-3',
    chapter: 4,
    title: 'You\'re Ready! ',

    icon: <Trophy size={24} color={POKER_COLORS.gold} />,
    content: (
      <VStack spacing={5} align="stretch">
        <Box 
          textAlign="center" 
          py={{ base: 4, md: 6 }}
          animation={`${celebrateAnim} 1s ease-in-out`}
        >
          <VStack spacing={3}>
            <Box bg={`${POKER_COLORS.gold}22`} p={4} borderRadius="full">
              <Trophy size={48} color={POKER_COLORS.gold} />
            </Box>
            <Heading size={{ base: 'md', md: 'lg' }} color="white"> Congratulations!</Heading>

            <Text color="gray.200" fontSize={{ base: 'md', md: 'lg' }}>
              You've learned the basics of Texas Hold'em!
            </Text>
          </VStack>
        </Box>
        
        <Box bg="blackAlpha.400" p={{ base: 3, md: 4 }} borderRadius="lg">
          <Text color="white" fontWeight="bold" mb={3} fontSize={{ base: 'sm', md: 'md' }}> Quick Review:</Text>

          <VStack spacing={2} align="start">
            <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }}> 2 hole cards + 5 community cards</Text>

            <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }}> Make best 5-card hand</Text>

            <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }}> Win by best hand OR making others fold</Text>

            <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }}> Position matters - late is best!</Text>

            <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }}> Play tight - fold weak hands</Text>

          </VStack>
        </Box>
        
        <Box bg={`${POKER_COLORS.gold}22`} p={{ base: 3, md: 4 }} borderRadius="lg" border="1px solid" borderColor={POKER_COLORS.gold}>
          <HStack spacing={3}>
            <Gift size={24} color={POKER_COLORS.gold} />
            <VStack align="start" spacing={0}>
              <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Completion Reward</Text>
              <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }}>Start training to earn FREE PSS Coins!</Text>
            </VStack>
          </HStack>
        </Box>
      </VStack>
    ),
  },
];

// ============================================================================
// CHAPTER INFO
// ============================================================================

const CHAPTERS = [
  { id: 1, title: 'What is Poker?', icon: '', steps: 3 },

  { id: 2, title: 'The Cards', icon: '🃏', steps: 3 },
  { id: 3, title: 'Game Flow', icon: '', steps: 3 },

  { id: 4, title: 'Basic Strategy', icon: '', steps: 3 },

];

// ============================================================================
// MAIN COMPONENT - MOBILE OPTIMIZED
// ============================================================================

const Poker101Modal: React.FC<Poker101ModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  initialStep = 0,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [quizAnswers, setQuizAnswers] = useState<Record<number, { answered: boolean; correct: boolean; selected: number | null }>>({});
  const [score, setScore] = useState(0);
  const toast = useToast();
  
  //  MOBILE: Detect screen size

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  
  const totalSteps = TUTORIAL_STEPS.length;
  const currentTutorialStep = TUTORIAL_STEPS[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;
  
  // Get current chapter
  const currentChapter = CHAPTERS.find(c => c.id === currentTutorialStep.chapter);
  
  // Check if step has quiz
  const hasQuiz = currentTutorialStep.quiz !== undefined;
  const quizAnswered = quizAnswers[currentStep]?.answered || false;
  
  // Check if can proceed
  const canProceed = !hasQuiz || quizAnswered;
  
  const handleQuizAnswer = useCallback((correct: boolean) => {
    setQuizAnswers(prev => ({
      ...prev,
      [currentStep]: { answered: true, correct, selected: null },
    }));
    
    if (correct) {
      setScore(prev => prev + 10);
      toast({
        title: 'Correct!',

        status: 'success',
        duration: 1500,
        position: 'top',
      });
    } else {
      toast({
        title: 'Not quite - check the explanation!',
        status: 'warning',
        duration: 2000,
        position: 'top',
      });
    }
    
    setCompletedSteps(prev => new Set([...prev, currentStep]));
  }, [currentStep, toast]);
  
  const handleNext = useCallback(() => {
    if (!canProceed) {
      toast({
        title: 'Answer the quiz first!',
        status: 'info',
        duration: 1500,
        position: 'top',
      });
      return;
    }
    
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Tutorial complete!
      onComplete();
      toast({
        title: 'Tutorial Complete!',

        description: `You scored ${score + (canProceed && hasQuiz ? 10 : 0)} points!`,
        status: 'success',
        duration: 3000,
        position: 'top',
      });
    }
  }, [currentStep, totalSteps, canProceed, hasQuiz, score, onComplete, toast]);
  
  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);
  
  const handleSkip = useCallback(() => {
    onClose();
    toast({
      title: 'Tutorial skipped',
      description: 'You can restart it anytime from Settings',
      status: 'info',
      duration: 2000,
    });
  }, [onClose, toast]);
  
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size={isMobile ? 'full' : 'xl'}
      closeOnOverlayClick={true}
      isCentered={!isMobile}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(8px)" />
      <ModalContent
        bg={POKER_COLORS.darkTeal}
        borderRadius={isMobile ? '0' : '2xl'}
        border={isMobile ? 'none' : '1px solid'}
        borderColor={`${POKER_COLORS.cyan}33`}
        maxH={isMobile ? '100dvh' : '90vh'}
        h={isMobile ? '100dvh' : 'auto'}
        overflow="hidden"
        mx={isMobile ? 0 : 4}
        my={isMobile ? 0 : 'auto'}
        position="relative"
        display="flex"
        flexDirection="column"
      >
        {/*  MOBILE: Prominent X close button */}

        <IconButton
          aria-label="Close tutorial"
          icon={<X size={isMobile ? 20 : 24} />}
          position="absolute"
          top={isMobile ? 2 : 3}
          right={isMobile ? 2 : 3}
          zIndex={10}
          variant="ghost"
          color="gray.400"
          _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
          onClick={onClose}
          size={isMobile ? 'sm' : 'md'}
        />
        
        <ModalBody p={0} display="flex" flexDirection="column" h="100%">
          {/* Header - MOBILE OPTIMIZED */}
          <Box
            bgGradient={`linear(135deg, ${POKER_COLORS.darkTeal} 0%, #1a0a2e 100%)`}
            p={isMobile ? 3 : 4}
            pr={isMobile ? 10 : 12}
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
            flexShrink={0}
          >
            <VStack spacing={isMobile ? 2 : 3}>
              {/* Title Row */}
              <HStack justify="space-between" w="100%">
                <HStack spacing={2}>
                  <Box bg={`${POKER_COLORS.gold}22`} p={isMobile ? 1.5 : 2} borderRadius="lg">
                    <BookOpen size={isMobile ? 16 : 20} color={POKER_COLORS.gold} />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Text color={POKER_COLORS.gold} fontSize="xs" fontWeight="bold">
                      POKER 101
                    </Text>
                    <Text color="white" fontWeight="bold" fontSize={isMobile ? 'xs' : 'sm'} noOfLines={1}>
                      {currentChapter?.icon} {currentChapter?.title}
                    </Text>
                  </VStack>
                </HStack>
                <HStack spacing={2}>
                  <Badge bg={POKER_COLORS.cyan} color="white" fontSize="xs">
                    {currentStep + 1} / {totalSteps}
                  </Badge>
                  {/*  MOBILE: Smaller Skip button */}

                  {!isMobile && (
                    <PokerButton
                      variant="secondary"
                      size="sm"
                      onClick={handleSkip}
                      px={3}
                    >
                      Skip Tutorial
                    </PokerButton>
                  )}
                </HStack>
              </HStack>
              
              {/* Progress Bar */}
              <Box w="100%">
                <Progress
                  value={progress}
                  size="sm"
                  colorScheme="cyan"
                  bg="blackAlpha.400"
                  borderRadius="full"
                />
              </Box>
              
              {/* Chapter Progress - MOBILE OPTIMIZED */}
              <HStack spacing={isMobile ? 1 : 2} w="100%">
                {CHAPTERS.map((chapter) => {
                  const chapterStart = TUTORIAL_STEPS.findIndex(s => s.chapter === chapter.id);
                  const isCurrentChapter = currentTutorialStep.chapter === chapter.id;
                  const isCompleted = chapterStart + chapter.steps <= currentStep;
                  
                  return (
                    <Box
                      key={chapter.id}
                      flex={1}
                      p={isMobile ? 1 : 2}
                      bg={isCurrentChapter ? `${POKER_COLORS.cyan}22` : 'blackAlpha.300'}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={isCurrentChapter ? POKER_COLORS.cyan : 'transparent'}
                    >
                      <HStack spacing={1} justify="center">
                        <Text fontSize={isMobile ? 'xs' : 'sm'}>{chapter.icon}</Text>
                        {isCompleted && <CheckCircle size={isMobile ? 10 : 12} color={POKER_COLORS.darkGreen} />}
                      </HStack>
                    </Box>
                  );
                })}
              </HStack>
            </VStack>
          </Box>
          
          {/* Content - MOBILE OPTIMIZED */}
          <Box 
            p={isMobile ? 4 : 6}
            flex={1}
            overflowY="auto"
            minH={0}
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { background: POKER_COLORS.cyan, borderRadius: '2px' },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <VStack spacing={isMobile ? 4 : 6} align="stretch">
              {/* Step Title */}
              <HStack spacing={isMobile ? 2 : 3}>
                <Box bg={`${POKER_COLORS.cyan}22`} p={isMobile ? 1.5 : 2} borderRadius="lg">
                  {currentTutorialStep.icon}
                </Box>
                <Heading size={isMobile ? 'sm' : 'md'} color="white">
                  {currentTutorialStep.title}
                </Heading>
              </HStack>
              
              {/* Visual Cards (if any) - MOBILE OPTIMIZED */}
              {currentTutorialStep.visualCards && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CardDisplay 
                    cards={currentTutorialStep.visualCards} 
                    label="Example"
                    size={isMobile ? 'md' : 'lg'}
                    isMobile={isMobile}
                  />
                </Box>
              )}
              
              {/* Main Content */}
              {currentTutorialStep.content}
              
              {/* Pro Tip - MOBILE OPTIMIZED */}
              {currentTutorialStep.proTip && (
                <Box 
                  bg={`${POKER_COLORS.gold}15`} 
                  p={isMobile ? 3 : 4} 
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={`${POKER_COLORS.gold}44`}
                >
                  <HStack spacing={2}>
                    <Lightbulb size={isMobile ? 16 : 18} color={POKER_COLORS.gold} />
                    <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize={isMobile ? 'xs' : 'sm'}>PRO TIP</Text>
                  </HStack>
                  <Text color="gray.200" fontSize={isMobile ? 'sm' : 'md'} mt={2}>
                    {currentTutorialStep.proTip}
                  </Text>
                </Box>
              )}
              
              {/* Quiz (if any) - MOBILE OPTIMIZED */}
              {currentTutorialStep.quiz && (
                <QuizSection
                  quiz={currentTutorialStep.quiz}
                  onAnswer={handleQuizAnswer}
                  answered={quizAnswered}
                  selectedAnswer={quizAnswers[currentStep]?.selected || null}
                  isMobile={isMobile}
                />
              )}
            </VStack>
          </Box>
          
          {/* Footer Navigation - MOBILE OPTIMIZED */}
          <Box
            p={isMobile ? 3 : 4}
            borderTop="1px solid"
            borderColor="whiteAlpha.100"
            bg={POKER_COLORS.darkTeal}
            flexShrink={0}
            //  MOBILE: Add safe area padding for iPhone notch

            pb={isMobile ? 'max(16px, env(safe-area-inset-bottom))' : 4}
          >
            <HStack justify="space-between" align="center">
              <PokerButton
                variant="ghost"
                leftIcon={<ChevronLeft size={isMobile ? 16 : 18} />}
                onClick={handlePrevious}
                isDisabled={currentStep === 0}
                size={isMobile ? 'md' : 'md'}
                px={isMobile ? 3 : 4}
                minW={isMobile ? '60px' : 'auto'}
                h={isMobile ? '48px' : 'auto'}
              >
                {isMobile ? '' : 'Back'}
              </PokerButton>
              
              {/*  MOBILE: Progress dots */}

              <HStack spacing={isMobile ? 1 : 2}>
                {[...Array(Math.min(isMobile ? 3 : 5, totalSteps))].map((_, i) => {
                  const stepIndex = Math.max(0, currentStep - (isMobile ? 1 : 2)) + i;
                  if (stepIndex >= totalSteps) return null;
                  
                  return (
                    <Box
                      key={stepIndex}
                      w={isMobile ? '6px' : '8px'}
                      h={isMobile ? '6px' : '8px'}
                      borderRadius="full"
                      bg={stepIndex === currentStep 
                        ? POKER_COLORS.cyan 
                        : completedSteps.has(stepIndex) 
                          ? POKER_COLORS.darkGreen 
                          : 'gray.600'
                      }
                    />
                  );
                })}
              </HStack>
              
              <PokerButton
                variant="primary"
                rightIcon={currentStep === totalSteps - 1 ? <Trophy size={isMobile ? 16 : 18} /> : <ChevronRight size={isMobile ? 16 : 18} />}
                onClick={handleNext}
                isDisabled={!canProceed}
                animation={canProceed ? `${pulseGlow} 2s ease-in-out infinite` : undefined}
                size={isMobile ? 'md' : 'md'}
                px={isMobile ? 4 : 4}
                minW={isMobile ? '80px' : 'auto'}
                h={isMobile ? '48px' : 'auto'}
              >
                {currentStep === totalSteps - 1 ? (isMobile ? 'Done!' : 'Complete!') : 'Next'}
              </PokerButton>
            </HStack>
            
            {/*  MOBILE: Skip link at bottom */}

            {isMobile && (
              <Text 
                color="gray.500" 
                fontSize="xs" 
                textAlign="center" 
                mt={3}
                py={2}
                cursor="pointer"
                _hover={{ color: 'gray.300' }}
                _active={{ color: 'white' }}
                onClick={handleSkip}
              >
                Skip tutorial
              </Text>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default Poker101Modal;